import { ReloadOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import moment from 'moment';
import {
  useRef,
  useState,
  useCallback,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DATE_FORMAT,
  DEFAULT_FONT_SIZE,
  GENERAL_TIMEOUT,
} from '../../../constants/systemConstants';
import {
  NivoPieChartData,
  TaskEnum,
  TaskGoodsDistributionData,
} from '../../../types';
import { alertMessage } from '../../../utils/alertMessage';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import FiveHundred from '../../FiveHundred';
import FourZeroThree from '../../FourZeroThree';
import { addCommasPrice, getPercent } from '../../../utils/helperFunction';
import { compareLastDataGrowthAnalysis } from '../../../utils/colComponents';
import { ResponsivePie } from '@nivo/pie';
import { nivoToolTip } from '../../../utils/nivoGraphTools';

type TaskGoodsDistributionProps = {
  taskEnum?: TaskEnum;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
};

const DefaultDataValue = {
  totalNum: '',
  successNum: '',
  notStartNum: '',
  differenceNum: '',
  unusualNum: '',
  successRate: '',
  notStartRate: '',
  differenceRate: '',
  unusualRate: '',
};

const TaskGoodsDistribution = ({
  taskEnum,
  isLoading,
  setIsLoading,
}: TaskGoodsDistributionProps) => {
  //General Components
  const [fourZeroThree, setFourZeroThree] = useState<boolean>(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const { RangePicker } = DatePicker;
  const isSubscribed = useRef(true);
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  //Data Components
  //This Year range
  const [currentRangeData, setCurrentRangeData] =
    useState<TaskGoodsDistributionData>();
  //Last Year range
  const [lastYearData, setLastYearData] = useState<TaskGoodsDistributionData>();
  const [growthData, setGrowthData] = useState<typeof DefaultDataValue>();
  //Text Components
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const [pieGraphData, setPieGraphData] = useState<NivoPieChartData[]>([]);

  /**
   * Grabs all the needed data for Task Goods Distribution
   */
  const getData = useCallback(
    (date?: { key: string; start: string; end: string }) => {
      const getPieGraphData = (data: TaskGoodsDistributionData) => {
        const pieGraph: NivoPieChartData[] = [];

        Object.entries(data).forEach(([key, value]) => {
          if (key.includes('Rate')) {
            const base = key.replace(/Rate$/i, ''); // e.g. "successRate" → "success"
            const labelKey = `${base}Num` as keyof TaskGoodsDistributionData;

            pieGraph.push({
              id: `${t(`analysis.taskGoodsDistribution.${key}`)}`, // use key name as id
              value: parseFloat(value), // parse rate value
              label: data[labelKey], // optional: label with %
            });
          }
        });

        return pieGraph;
      };

      if (isSubscribed.current) setIsLoading(true);
      getDataWithAuthToken('analysis/task/task_goods', {
        params: {
          startDate: date
            ? date.start
            : form.getFieldValue('date')
            ? moment(form.getFieldValue('date')[0]).format(DATE_FORMAT)
            : undefined,
          endDate: date
            ? date.end
            : form.getFieldValue('date')
            ? moment(form.getFieldValue('date')[1]).format(DATE_FORMAT)
            : undefined,
          taskStage: form.getFieldValue('taskStage')
            ? form.getFieldValue('taskStage')
            : undefined,
        },
      })
        .then((response) => {
          if (response && response.goodStatus) {
            if (isSubscribed.current) {
              const resData: TaskGoodsDistributionData = response.data;
              if (date) {
                if (date.key === 'range1') {
                  setCurrentRangeData(resData);
                  setPieGraphData(getPieGraphData(resData));
                } else if (date.key === 'range2') {
                  setLastYearData(resData);
                }
              } else {
                setCurrentRangeData(resData);
                setPieGraphData(getPieGraphData(resData));
              }
            }
          } else if (response && response.returnCode === 403) {
            if (isSubscribed.current) setFourZeroThree(true);
          } else {
            setFiveHundred(true);
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
          if (isSubscribed.current) setIsLoading(false);
        })
        .catch((err) => {
          console.log(err);
          if (isSubscribed.current) setIsLoading(false);
        });
    },
    [t, form, setFourZeroThree, setFiveHundred, setIsLoading]
  );

  const getGrowth = useCallback(() => {
    let growthObj: any = DefaultDataValue;
    if (currentRangeData && lastYearData) {
      console.log('currentRangeData =', currentRangeData);
      console.log('lastYearData =', lastYearData);
      Object.keys(currentRangeData).forEach((label) => {
        growthObj[label] = getPercent(
          lastYearData[label as keyof typeof lastYearData],
          currentRangeData[label as keyof typeof currentRangeData]
        );
      });
      setGrowthData(growthObj);
    }
  }, [currentRangeData, lastYearData]);

  const refreshData = () => {
    if (form.getFieldValue('date') && form.getFieldValue('date').length === 2) {
      setCurrentRangeData(undefined);
      setLastYearData(undefined);
      setPieGraphData([]);
      let start = moment(form.getFieldValue('date')[0]);
      let end = moment(form.getFieldValue('date')[1]);
      getData({
        key: 'range1',
        start: start.format(DATE_FORMAT),
        end: end.format(DATE_FORMAT),
      });
      getData({
        key: 'range2',
        start: start.subtract(1, 'years').format(DATE_FORMAT),
        end: end.subtract(1, 'years').format(DATE_FORMAT),
      });
    } else {
      setCurrentRangeData(undefined);
      setLastYearData(undefined);
      setPieGraphData([]);
      setGrowthData(undefined);
      getData();
    }
  };

  useEffect(() => {
    getGrowth();
  }, [getGrowth]);

  useEffect(() => {
    getData({
      key: 'range1',
      start: moment().subtract(8, 'days').format(DATE_FORMAT),
      end: moment().subtract(1, 'days').format(DATE_FORMAT),
    });
    getData({
      key: 'range2',
      start: moment()
        .subtract(8, 'days')
        .subtract(1, 'years')
        .format(DATE_FORMAT),
      end: moment()
        .subtract(1, 'days')
        .subtract(1, 'years')
        .format(DATE_FORMAT),
    });
  }, [getData]);

  return (
    <>
      {fourZeroThree ? (
        <FourZeroThree />
      ) : fiveHundred ? (
        <FiveHundred />
      ) : (
        <Spin style={{ width: '100%' }} spinning={isLoading}>
          <Form /**Range picker, Task Stage, OK, Reset, Refresh Buttons */
            form={form}
            ref={formRef}
            initialValues={{}}
          >
            <Row gutter={[16, 0]}>
              <Col span={24} md={24} lg={8} xl={8}>
                <Form.Item
                  name={'date'}
                  label={t('analysis.orderDistribution.date')}
                  initialValue={[
                    moment().subtract(8, 'days'),
                    moment().subtract(1, 'days'),
                  ]}
                >
                  <RangePicker
                    style={{ width: '100%' }}
                    format={DATE_FORMAT}
                    placeholder={[
                      t('analysis.orderDistribution.startDate'),
                      t('analysis.orderDistribution.endDate'),
                    ]}
                    ranges={{
                      [`${t('nivo.week')}`]: [
                        moment().startOf('isoWeek'),
                        moment().endOf('isoWeek'),
                      ],
                      [`${t('nivo.month')}`]: [
                        moment().startOf('month'),
                        moment().endOf('month'),
                      ],
                      [`${t('nivo.firstHalfYear')}`]: [
                        moment().startOf('year'),
                        moment().startOf('year').add(6, 'months'),
                      ],
                      [`${t('nivo.secondHalfYear')}`]: [
                        moment().startOf('year').add(6, 'months'),
                        moment().endOf('year'),
                      ],
                      [`${t('nivo.year')}`]: [
                        moment().startOf('year'),
                        moment().endOf('year'),
                      ],
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Space align="start">
                  <Button
                    htmlType="submit"
                    type="primary"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          refreshData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.ok')}
                  </Button>
                  <Button
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          setGrowthData(undefined);
                          setCurrentRangeData(undefined);
                          setLastYearData(undefined);
                          setPieGraphData([]);
                          form.resetFields();

                          getData({
                            key: 'range1',
                            start: moment()
                              .subtract(8, 'days')
                              .format(DATE_FORMAT),
                            end: moment()
                              .subtract(1, 'days')
                              .format(DATE_FORMAT),
                          });
                          getData({
                            key: 'range2',
                            start: moment()
                              .subtract(8, 'days')
                              .subtract(1, 'years')
                              .format(DATE_FORMAT),
                            end: moment()
                              .subtract(1, 'days')
                              .subtract(1, 'years')
                              .format(DATE_FORMAT),
                          });
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                  >
                    {t('general.reset')}
                  </Button>
                </Space>
                <Tooltip title={t('general.refresh')}>
                  <Button
                    type="text"
                    onClick={() => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      setTypingTimeout(
                        setTimeout(() => {
                          refreshData();
                        }, GENERAL_TIMEOUT)
                      );
                    }}
                    icon={<ReloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>
          </Form>
          {currentRangeData /**Overview data */ && (
            <Row style={{ width: '100%', paddingTop: 15 }}>
              {Object.keys(currentRangeData)
                .filter(
                  (label) =>
                    currentRangeData[label as keyof typeof currentRangeData] !=
                    null
                )
                .map((label: string, index) => {
                  let growthValue = growthData
                    ? growthData[label as keyof typeof growthData]
                    : '';
                  return (
                    <Col key={index} span={24} sm={12} md={6} lg={6}>
                      <div
                        style={{
                          textAlign: 'center',
                        }}
                      >
                        {t(`analysis.taskGoodsDistribution.${label}`)}
                      </div>
                      <Typography.Title
                        level={3}
                        style={{
                          margin: 0,
                          textAlign: 'center',
                        }}
                      >
                        {addCommasPrice(
                          currentRangeData[
                            label as keyof typeof currentRangeData
                          ]
                        )}
                      </Typography.Title>
                      {growthData &&
                        !Object.keys(growthData).every(
                          (label: any) =>
                            growthData[label as keyof typeof growthData] === ''
                        ) && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                            }}
                          >
                            <Form.Item
                              style={{
                                textAlign: 'center',
                                marginBottom: 0,
                              }}
                              label={t(`analysis.orderOverview.lastYear`)}
                            >
                              {compareLastDataGrowthAnalysis(growthValue)}
                            </Form.Item>
                          </div>
                        )}
                    </Col>
                  );
                })}
            </Row>
          )}

          {pieGraphData.length > 0 && (
            <Col
              span={24}
              style={{
                height: 300,
              }}
            >
              <div style={{ textAlign: 'center' }}>
                {t(`analysis.taskGoodsDistribution.taskGoodsPackingAnalysis`)}
              </div>
              <ResponsivePie
                colors={{ scheme: 'paired' }}
                tooltip={(datum) =>
                  nivoToolTip({
                    ...datum,
                    tooltipType: 'Standard',
                  })
                }
                data={pieGraphData}
                arcLinkLabelsStraightLength={10}
                arcLinkLabelsDiagonalLength={10}
                arcLabel={(d) => (d.value !== 0 ? `${d.value}%` : '')}
                arcLinkLabel={(d) => (d.value !== 0 ? `${d.id}` : '')}
                margin={{
                  top: 40,
                  left: 40,
                  right: 40,
                  bottom: 40,
                }}
                arcLinkLabelsSkipAngle={pieGraphData.length < 3 ? 0 : 5}
                arcLabelsSkipAngle={pieGraphData.length < 3 ? 0 : 20}
                theme={{
                  fontSize: 12,
                  axis: {
                    legend: { text: { fontSize: DEFAULT_FONT_SIZE } },
                  },
                }}
              />
            </Col>
          )}
        </Spin>
      )}
    </>
  );
};

export default TaskGoodsDistribution;
